/*
 * <read-more> — progressive-enhancement Read more / Read less for the collection
 * secondary description (matches PCP_Collection_LearnMore_FAQ_Mockup.html).
 *
 * The lead (heading + first paragraph) and the remainder are separate, server-rendered
 * markup. With JS OFF the remainder is grid-template-rows:1fr, so the full text stays
 * visible (nothing trapped). On connect this element adds `is-enhanced` — the section
 * CSS then collapses the remainder to grid-template-rows:0fr — reveals the toggle, and
 * wires it. Clicking toggles `is-open`, which uncollapses the remainder (animated via
 * grid-template-rows in the section CSS) and (via CSS) swaps the More/Less label and
 * rotates the arrow. The collapsed remainder is marked `inert` so its focusable children
 * leave the tab order and the accessibility tree while hidden; focus stays on the toggle.
 *
 * Collapse/expand is animated in CSS (grid-template-rows 0fr<->1fr). The FIRST collapse
 * on connect is done with transitions momentarily disabled, so the section doesn't appear
 * to close itself on load — only user toggles animate.
 *
 * On "Read less" the read-more element's top is scrolled assertively into view
 * (block:'start'), so collapsing a long remainder returns the reader up to the Read more
 * button instead of stranding them where the content used to be. Only on collapse.
 *
 * Follows the theme's own custom-element convention (customElements.get guard,
 * cf. AccordionTab in global.js and vp-show-more.js). 
 */
if (!customElements.get('read-more')) {
  customElements.define(
    'read-more',
    class ReadMore extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.read-more__toggle');
        this.more = this.querySelector('.read-more__more');
        if (!this.toggle || !this.more) return; // no collapsible remainder -> nothing to do

        // Collapse on connect WITHOUT animating the initial close (avoids a
        // collapse-on-load flash); transitions apply to user toggles only.
        this.more.style.transition = 'none';
        this.classList.add('is-enhanced'); // CSS collapses the remainder to 0fr
        this.setState(false);
        void this.more.offsetHeight; // force reflow so the collapse is applied instantly
        this.more.style.transition = '';

        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          const willOpen = !this.classList.contains('is-open');
          this.setState(willOpen);
          this.toggle.focus();
          if (!willOpen) {
            // Collapsing: assertively return the reader UP to the Read more button.
            // Scroll the read-more element's TOP into view (block:'start') rather than
            // 'nearest' (which barely moves when the button is already partly visible)
            // or 'center'/the toggle itself (whose position is mid-travel during the
            // collapse animation, so it can scroll the wrong way). The element top is
            // stable through the collapse, and the collapsed block (heading + first
            // paragraph + toggle) is short, so the button lands clearly in view.
            // scroll-margin-top on .read-more keeps it clear of the sticky header.
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
          }
        });
      }

      setState(open) {
        this.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.more.inert = !open;
        this.more.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
    }
  );
}
